import React, { useState } from 'react';

import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import AttachmentsModal from 'bundles/apex/tasks/attachment_modal';
import { refreshActivities } from 'bundles/apex/activities/hooks';
import { showSnackbar } from 'components/ui/snackbar/helper';
import { deleteRequest } from 'helpers/api';

import type { Attachment, Task } from 'bundles/apex/tasks/types';
import { Button } from 'components/ui/button';

export type CheckboxState = 'idle' | 'anim' | 'settle' | 'checked';

interface UndoControlProps {
  onUndo: () => void | Promise<void>;
}

function UndoControl({ onUndo }: UndoControlProps) {
  const [undone, setUndone] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const handleUndo = async () => {
    setDisabled(true);
    await onUndo();
    setUndone(true);
  };

  if (undone) {
    return (
      <Button
        className="border-success text-success pe-none"
        iconEnd={faCheck}
        render={<span />}
        size="sm"
        variant="outline"
      >
        Undone
      </Button>
    );
  }

  return (
    <Button
      disabled={disabled}
      size="sm"
      variant="outline"
      onClick={handleUndo}
    >
      Undo
    </Button>
  );
}

interface Props {
  addTaskAttachment: (task: Task, attachment: Attachment) => void;
  attachmentsPath: string;
  deleteTaskAttachment: (task: Task, attachment: Attachment) => void;
  features?: {
    taskAttachmentExceptions?: boolean;
  };
  restoreTask?: (task: Task) => void;
  state?: CheckboxState;
  task: Task;
  onUpdate: (task: Task) => Promise<Task>;
}

export default function Status({
  addTaskAttachment,
  attachmentsPath,
  deleteTaskAttachment,
  features,
  restoreTask,
  state = 'idle',
  task,
  onUpdate,
}: Props) {
  const [showAttachmentsModal, setShowAttachmentsModal] = useState(false);

  function onModalSave(task) {
    onUpdate({
      ...task,
      completed: task.attachments.length > 0 || !!task.attachmentExceptionReason,
    });
  }

  function onModalHide() {
    onModalSave(task);
    setShowAttachmentsModal(false);
  }

  async function toggleComplete() {
    if (
      !task.completed && task.requiresAttachment && task.attachments.length === 0 &&
      (!features?.taskAttachmentExceptions || !task.attachmentExceptionReason)
    ) {
      setShowAttachmentsModal(true);
    } else {
      const nextCompleted = !task.completed;
      const previousCompleted = task.completed;
      const savedTask = await onUpdate({ ...task, completed: nextCompleted });
      // Completing a task with a type creates an activity; undo should remove it too.
      const createdActivityId = savedTask.createdActivityId;
      showSnackbar({
        text: (
          <div className="d-flex flex-column">
            {nextCompleted ? 'Task completed' : 'Task marked incomplete'}
            <UndoControl
              onUndo={async () => {
                const restored = await onUpdate({ ...task, completed: previousCompleted });
                restoreTask?.(restored);
                if (createdActivityId) {
                  await deleteRequest(`/apex/activities/${createdActivityId}`);
                  refreshActivities();
                }
              }}
            />
          </div>
        ),
        variant: 'success',
      });
    }
  }

  return <>
    <button
      aria-checked={!!task.completed}
      aria-label={task.completed ? 'Mark task incomplete' : 'Mark task complete'}
      className="task-checkbox"
      data-state={state}
      role="checkbox"
      type="button"
      onClick={toggleComplete}
    >
      <FontAwesomeIcon aria-hidden="true" icon={faCheck} />
    </button>

    <AttachmentsModal
      addTaskAttachment={attachment => addTaskAttachment(task, attachment)}
      attachmentExceptions={features?.taskAttachmentExceptions}
      attachmentsPath={attachmentsPath}
      deleteTaskAttachment={attachment => deleteTaskAttachment(task, attachment)}
      show={showAttachmentsModal}
      task={task}
      onHide={onModalHide}
      onSave={onModalSave}
    />
  </>;
}
